import alt from "../alt";
import indexBy from "lodash/keyBy";
import extend from "lodash/extend";
import values from "lodash/values";
import TicketUtil from "../utils/TicketUtil";
import TicketActions from "../actions/TicketActions";
import UserStore from "../stores/UserStore";
import OrderStore from "../stores/OrderStore";
import OrderActions from "../actions/OrderActions";
import LedgerActions from "../actions/LedgerActions";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

class TicketStore {
  constructor() {
    this.bindActions(TicketActions);

    this.state = {
      tickets: {},
      ticketTransferHistory: [],
      ticketTransferHistorySingleTicket: {},
      isFetching: false
    };

    this.exportPublicMethods({
      getTicketById: this.getTicketById.bind(this),
      getTicketsForPerson: this.getTicketsForPerson.bind(this),
      getTicketsForEvent: this.getTicketsForEvent.bind(this),
      getTicketTransferHistory: this.getTicketTransferHistory.bind(this),
      getTicketTransferHistorySingleTicket: this.getTicketTransferHistorySingleTicket.bind(this)
    });
  }

  getTicketById(id) {
    return this.state.tickets[id];
  }

  getTicketsForPerson(id, eventBucket) {
    return values(this.state.tickets).filter(function (t) {
      if (eventBucket && t.event_bucket !== eventBucket) {
        return false;
      }
      return t.person && t.person.id === id;
    });
  }

  getTicketsForEvent(id) {
    return values(this.state.tickets).filter(function (t) {
      return t.event.id === id;
    });
  }

  getTicketTransferHistory() {
    return this.state.ticketTransferHistory;
  }

  getTicketTransferHistorySingleTicket() {
    return this.state.ticketTransferHistorySingleTicket;
  }

  onFetchAllForEvent(eventId) {
    this.setState({ isFetching: true });
    TicketUtil.fetchAllForEvent(UserStore.getState().apiKey, eventId)
      .then(data => {
        this.setState({ isFetching: false });
        TicketActions.updateTickets(indexBy(data, "id"));
      })
      .catch(TicketActions.fetchFailed);
  }

  onFetchAllForPerson(personId) {
    this.setState({ isFetching: true });
    TicketUtil.fetchAllForPerson(UserStore.getState().apiKey, personId)
      .then(data => {
        this.setState({ isFetching: false });
        TicketActions.updateTickets(indexBy(data, "id"));
      })
      .catch(TicketActions.fetchFailed);
  }

  onFetchOne(id) {
    this.setState({ isFetching: true });
    TicketUtil.fetchOne(UserStore.getState().apiKey, id)
      .then(data => {
        this.setState({ isFetching: false });
        TicketActions.updateTicket(id, data);
      })
      .catch(TicketActions.fetchFailed);
  }

  onFetchTicketTransferHistory(ids) {
    this.setState({ isFetching: true });
    TicketUtil.fetchTicketTransferHistory(UserStore.getState().apiKey, ids.ticketId, ids.eventBucketId)
      .then(data => {
        this.setState({ ticketTransferHistory: data.history, ticketTransferHistorySingleTicket: data.ticket, isFetching: false });
      })
      .catch(TicketActions.fetchFailed);
  }

  onDelete(id) {
    TicketUtil.delete(UserStore.getState().apiKey, UserStore.getCredentials().api_key, id).then(data => {
      delete this.state.tickets[id];
      this.setState(this.state);
    });
  }

  onMarkAsUsed(id) {
    TicketUtil.markAsUsed(UserStore.getState().apiKey, id).then(data => {
      this.state.tickets[id] = data;
      this.setState(this.state);
    });
  }

  onUpdateTicket(data) {
    this.state.tickets[data.id] = data.ticket;
    this.setState(this.state);
  }

  onUpdateTickets(tickets) {
    extend(this.state.tickets, tickets);
    this.setState(this.state);
  }

  onFetchFailed(e) {
    this.setState({ isFetching: false });
    alert(`Fetching ticket failed: ${e.message}`);
    throw e;
  }

  onDownloadPdf(options) {
    TicketUtil.downloadPdf(UserStore.getState().apiKey, options);
  }

  onChangeTicketsUnlock(form) {
    this.setState({ isFetching: true });
    TicketUtil.changeTicketsUnlock(UserStore.getState().apiKey, form)
      .then(data => {
        const tickets = { ...this.state.tickets };
        data.tickets.forEach(({ id, unlocked_until, expires_at }) => {
          if (tickets[id]) {
            tickets[id].unlocked_until = unlocked_until;
            tickets[id].expires_at = expires_at;
          }
        });

        // Dispatch order update if order has been fetched previously
        // (to propagate updated tickets if viewing orders page).
        const orderId = data.tickets.length && data.tickets[0].order;
        let order = OrderStore.getState().orders[orderId];
        if (order) {
          OrderActions.fetchOne(orderId);
        }

        this.setState({ isFetching: false, tickets });
      })
      .catch(TicketActions.fetchFailed);
  }

  onTransfer({ ids, recipient }) {
    TicketUtil.transfer(UserStore.getState().apiKey, ids, recipient)
      .then(data => {
        data.tickets.forEach(ticket => {
          TicketActions.updateTicket(ticket.id, {
            ...this.state.tickets[ticket.id],
            pending_transfer: ticket.pending_transfer
          });
          // Dispatch order update if order has been fetched previously
          // (to propagate updated ticket if viewing orders page).
          let order = OrderStore.getState().orders[ticket.order];
          if (order) {
            OrderActions.fetchOne(ticket.order);
          }
        });
      })
      .catch(TicketActions.editFailed);
  }

  onCancelTransfer(id) {
    TicketUtil.cancelTransfer(UserStore.getState().apiKey, id)
      .then(data => {
        data.tickets.forEach(ticket => {
          TicketActions.updateTicket(ticket.id, {
            ...this.state.tickets[ticket.id],
            pending_transfer: undefined
          });
          // Dispatch order update if order has been fetched previously
          // (to propagate updated ticket if viewing orders page).
          let order = OrderStore.getState().orders[ticket.order];
          if (order) {
            OrderActions.fetchOne(ticket.order);
          }
        });
      })
      .catch(TicketActions.editFailed);
  }

  onResendTransfer(id) {
    GlobalNotificationActions.notify.defer(
      `Resending ticket transfer email...`
    );

    TicketUtil.resendTransfer(UserStore.getState().apiKey, id)
      .then(data => {
        GlobalNotificationActions.notify.defer(
          `Ticket transfer email resent successfully.`
        );
      })
      .catch(TicketActions.editFailed);
  }

  onEditFailed(e) {
    alert(`Edit failed: ${e.message}`);
  }

  onDeleteTickets(form) {
    this.setState({ isFetching: true });

    const tickets = { ...this.state.tickets };
    TicketUtil.deleteBulk(UserStore.getState().apiKey, form)
      .then(data => {
        this.setState({ isFetching: false, tickets });
      })
      .catch(TicketActions.fetchFailed);
  }

  onExchangeTickets(form) {
    this.setState({ isFetching: true });
    TicketUtil.exchangeTickets(UserStore.getState().apiKey, form)
      .then(data => {
        const tickets = { ...this.state.tickets };
        data.tickets.forEach(
          ({
            id,
            ticket_bucket,
            event,
            event_bucket,
            unlocked_until,
            expires_at,
            seat
          }) => {
            if (tickets[id]) {
              tickets[id].ticket_bucket = ticket_bucket;
              tickets[id].event = event;
              tickets[id].event_bucket = event_bucket;
              tickets[id].unlocked_until = unlocked_until;
              tickets[id].expires_at = expires_at;
              tickets[id].seat = seat;
            }
          }
        );

        const orderId = data.tickets.length && data.tickets[0].order;
        let order = OrderStore.getState().orders[orderId];
        if (order) {
          OrderActions.fetchOne(orderId);
          LedgerActions.fetchLedgerTransactionsForOrder.defer(orderId); // force ledger update
        }

        this.setState({ isFetching: false, tickets });
      })
      .catch(TicketActions.fetchFailed);
  }

  onChangeSeats(tickets) {
    this.setState({ isFetching: true });
    TicketUtil.changeSeats(UserStore.getState().apiKey, tickets)
      .then(data => {
        const tickets = { ...this.state.tickets };
        data.tickets.forEach(({ id, seat }) => {
          if (tickets[id]) {
            tickets[id].seat = seat;
          }
        });

        const orderId = data.tickets.length && data.tickets[0].order;
        let order = OrderStore.getState().orders[orderId];
        if (order) {
          OrderActions.fetchOne(orderId);
        }

        this.setState({ tickets, isFetching: false });
      })
      .catch(TicketActions.fetchFailed);
  }
}

export default alt.createStore(TicketStore, "TicketStore");



// WEBPACK FOOTER //
// ./src/app/stores/TicketStore.js